import axios from 'axios';
import {apiBaseURL, apiUAE_MT_URL} from './Constants';

export const MIDDLE_EAST_DATA_SOURCE = "uae_mt";
export const MIDDLE_EAST_MARKETS = ["UAE MT", "KSA MT"] as const;

export const isMiddleEastMarket = (
  value: string | null | undefined,
): value is (typeof MIDDLE_EAST_MARKETS)[number] =>
  !!value &&
  (MIDDLE_EAST_MARKETS as readonly string[]).includes(value);


export const getApplicationUrl = (datasource: string | null) => {
  switch (datasource) {
    case MIDDLE_EAST_DATA_SOURCE:
      return apiUAE_MT_URL;
    default:
      return apiBaseURL;
  }
};
const normalize = (value: any) => String(value ?? "").trim().toLowerCase();

const resolveMarketForPayload = (data: any) => {
  const selectedMarket = localStorage.getItem("SelectedMarket") || "UAE MT";
  const dataSource = localStorage.getItem("DataSource");

  const projectCandidates = [
    data?.project_name,
    data?.projName,
    data?.projectName,
    data?.title,
  ]
    .map(normalize)
    .filter(Boolean);

  const isMiddleEastProject = projectCandidates.some(
    (value) =>
      value === MIDDLE_EAST_DATA_SOURCE ||
      value.includes("uae mt") ||
      value.includes("ksa mt") ||
      value.includes("middle east"),
  );

  if (!isMiddleEastProject && dataSource !== MIDDLE_EAST_DATA_SOURCE) {
    return null;
  }

  if (isMiddleEastMarket(selectedMarket)) {
    return selectedMarket;
  }

  if (projectCandidates.some((value) => value.includes("ksa mt"))) {
    return "KSA MT";
  }

  if (projectCandidates.some((value) => value.includes("uae mt"))) {
    return "UAE MT";
  }

  return selectedMarket;
};

export const appendMarketIfNeeded = (data: any) => {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return data;
  }

  if (data instanceof FormData) {
    return data;
  }

  if (Object.prototype.hasOwnProperty.call(data, "market")) {
    return data;
  }

  const market = resolveMarketForPayload(data);
  if (!market) {
    return data;
  }

  return {
    ...data,
    market,
  };
};


export const httpClient = {
  get: async (url: string, authToken?: string) => {
    const headers: any = {
      'Content-Type': 'application/json',
    };

    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const config: any = {
      headers: headers,
    };
    let apiUrl = getApplicationUrl(localStorage.getItem('DataSource'));

    const json = await axios.get(apiUrl + url, config);
    if (json.data.Success === false) {
      throw json.data;
    } else {
      return json.data;
    }
  },
  getFile: async (url: string, authToken?: string) => {
    const headers: any = {};

    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const config: any = {
      headers: headers,
      responseType: 'blob',
    };
    let apiUrl = getApplicationUrl(localStorage.getItem('DataSource'));

    const json = await axios.get(apiUrl + url, config);
    return json;
  },

  post: async (url: string, data: any, authToken: string) => {
    const skipMarketInjection =
      url === "/summary_sql_async" || url.endsWith("/summary_sql_async");
    const payloadWithMarket = skipMarketInjection
      ? data
      : appendMarketIfNeeded(data);
    const options = payloadWithMarket ? JSON.stringify(payloadWithMarket) : undefined;
    const headers: any = {
      'Content-Type': 'application/json',
    };

    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }
    let apiUrl = getApplicationUrl(localStorage.getItem('DataSource'));

    const json = await axios.post(apiUrl + url, options, {headers});
    if (json.data.Success === false) {
      throw json.data;
    } else {
      return json.data;
    }
  },
  postUpload: async (url: string, data: any, authToken: string) => {
    const options = data;
    const headers: any = {
      'Content-Type': 'multipart/form-data',
    };

    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }
    let apiUrl = getApplicationUrl(localStorage.getItem('DataSource'));

    const result = await axios.post(apiUrl + url, options, {headers});
    return result;
  },

  delete: async (url: string, data: any, authToken: string) => {
    const payloadWithMarket = appendMarketIfNeeded(data);
    const options = payloadWithMarket ? JSON.stringify(payloadWithMarket) : undefined;

    const headers: any = {
      'Content-Type': 'application/json',
    };

    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const config = {
      data: options,
      withCredentials: true,
      headers: headers,
    };

    return axios.delete(url, config).then((json) => {
      if (json.data.Success === false) {
        throw json.data;
      } else {
        return json.data;
      }
    });
  },

  put: async (url: string, data: any, authToken: string) => {
    const payloadWithMarket = appendMarketIfNeeded(data);
    const body = payloadWithMarket ? JSON.stringify(payloadWithMarket) : undefined;

    const headers: any = {
      'Content-Type': 'application/json',
    };

    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const config = {
      headers: headers,
    };
    let apiUrl = getApplicationUrl(localStorage.getItem('DataSource'));

    return axios.put(apiUrl + url, body, config).then((json) => {
      if (json.data.Success === false) {
        throw json.data;
      } else {
        return json.data;
      }
    });
  },
  postImage: async (url: string, data: any, authToken: string) => {
    const payloadWithMarket = appendMarketIfNeeded(data);
    const options = payloadWithMarket ? JSON.stringify(payloadWithMarket) : undefined;
    const headers: any = {
      'Content-Type': 'application/json',
      'User-Agent': 'insomnia/10.1.1',
      team_id: 'd597ec85-2bf5-4d25-90a4-de9d50f18ab2',
      project_id: '8eae1e0e-4fe9-4613-90f1-4a9d667d7c9f',
      'x-pepgenx-apikey': process.env.REACT_APP_PEPGEX_API_ENTRY,
      token_encoding: 'cl100k_base',
      embedding_model_name: 'text-embedding-3-small',
    };

    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }
    const json = await axios.post(url, options, {headers});
    if (json.data.Success === false) {
      throw json.data;
    } else {
      return json.data;
    }
  },
};
